import { queryOptions, useQuery } from '@tanstack/react-query';

import type {
  GetCatalogAnalogsQuery,
  GetProductAlsoboughtAndRecommendQuery
} from '@schema/cart';

import API from '@api';
import { withSsrCache } from '../../ssrCache';

/**
 * Рекомендательные блоки (карусели «Рекомендуем», «С этим товаром покупают»,
 * аналоги). Кэш как у остальных товарных блоков.
 */
const RECOMMENDATIONS_STALE_TIME = 5 * 60 * 1000;
const RECOMMENDATIONS_GC_TIME = 10 * 60 * 1000;

/** Рекомендуемые товары магазина/бара (bar-menu, sms-landing) */
export const recommendedByStoreQueryOptions = (
  storeId: number,
  productsLimit = 8
) =>
  queryOptions({
    queryKey: ['recommendedByStore', storeId, productsLimit],
    queryFn: () => API.getRecommendedByStore({ storeId, productsLimit }),
    select: res => res.getRecommendedByStore?.catalogItems ?? [],
    staleTime: RECOMMENDATIONS_STALE_TIME,
    gcTime: RECOMMENDATIONS_GC_TIME
  });

export const useRecommendedByStoreQuery = (
  storeId: number,
  productsLimit?: number
) => useQuery(recommendedByStoreQueryOptions(storeId, productsLimit));

/** Блок «Вас заинтересует» на странице поиска */
export const recommendItemsQueryOptions = () =>
  queryOptions({
    queryKey: ['recommendItems'],
    queryFn: () => API.getRecommendItems(),
    select: res => res.getRecommendItems ?? [],
    staleTime: RECOMMENDATIONS_STALE_TIME,
    gcTime: RECOMMENDATIONS_GC_TIME
  });

export const useRecommendItemsQuery = () =>
  useQuery(recommendItemsQueryOptions());

/**
 * Аналоги недоступного товара. xStoreId в ключе (в запрос не идёт): цены
 * зависят от магазина — смена перезапрашивает (ЭТАП 2, ProductPage).
 */
export const catalogAnalogsQueryOptions = (
  catalogId: number,
  epp = 10,
  xStoreId?: number | string | null
) =>
  queryOptions({
    queryKey: ['catalogAnalogs', catalogId, epp, xStoreId ?? null],
    queryFn: () => API.getCatalogAnalogs({ catalogId, epp }),
    select: res => res.getCatalogAnalogs ?? [],
    staleTime: RECOMMENDATIONS_STALE_TIME,
    gcTime: RECOMMENDATIONS_GC_TIME
  });

export const useCatalogAnalogsQuery = (catalogId: number, epp?: number) =>
  useQuery(catalogAnalogsQueryOptions(catalogId, epp));

/**
 * «С этим товаром покупают» + «Рекомендуем попробовать» (модалка на карточке
 * и страница товара — ЭТАП 2). xStoreId в ключе (в запрос не идёт): цены
 * зависят от магазина. isMenu/menuStoreId — карточки меню бара: идут и в
 * запрос, и в ключ. Они безличные (id бара из URL), поэтому SSR-кэш
 * выключает только xStoreId.
 */
export const productAlsoboughtQueryOptions = (
  productId: number,
  xStoreId?: number | string | null,
  isMenu = false,
  menuStoreId?: number
) =>
  withSsrCache(
    queryOptions({
      queryKey: [
        'productAlsobought',
        productId,
        isMenu,
        menuStoreId ?? null,
        xStoreId ?? null
      ],
      queryFn: () =>
        API.getProductAlsoboughtAndRecommend({
          productId,
          isMenu,
          menuStoreId
        }),
      staleTime: RECOMMENDATIONS_STALE_TIME,
      gcTime: RECOMMENDATIONS_GC_TIME
    }),
    'product',
    [xStoreId]
  );

// module-level: стабильная ссылка для select
const selectAlsobought = (res: GetProductAlsoboughtAndRecommendQuery) =>
  res.getProductAlsoboughtAndRecommend?.alsobought ?? [];

interface UseAlsoBoughtWithAnalogsReturn {
  alsoBought: NonNullable<
    GetProductAlsoboughtAndRecommendQuery['getProductAlsoboughtAndRecommend']
  >['alsobought'];
  analogs: GetCatalogAnalogsQuery['getCatalogAnalogs'];
  isPending: boolean;
}

/**
 * «С этим товаром покупают» с фолбэком на аналоги: аналоги запрашиваются,
 * только если alsobought пришёл пустым (модалка быстрого просмотра, позже
 * ProductPage). Ключ аналогов общий с AnalogsModal; когда фолбэк не нужен,
 * analogs пустой, даже если аналоги этого товара уже есть в кэше.
 */
export const useAlsoBoughtWithAnalogsQuery = (
  productId: number,
  analogsEpp = 10
): UseAlsoBoughtWithAnalogsReturn => {
  const alsoBoughtQuery = useQuery({
    ...productAlsoboughtQueryOptions(productId),
    select: selectAlsobought
  });
  const alsoBought = alsoBoughtQuery.data ?? [];

  const needAnalogs = alsoBoughtQuery.isSuccess && !alsoBought.length;
  const analogsQuery = useQuery({
    ...catalogAnalogsQueryOptions(productId, analogsEpp),
    enabled: needAnalogs
  });

  return {
    alsoBought,
    analogs: needAnalogs ? analogsQuery.data ?? [] : [],
    isPending:
      alsoBoughtQuery.isPending || (needAnalogs && analogsQuery.isPending)
  };
};

const selectThankYouPromotionProducts = (
  res: Awaited<ReturnType<typeof API.getThankYouPromotionProducts>>
) =>
  res.getThankYouPromotionProducts
    ? res.getThankYouPromotionProducts.map(item => ({ ...item, thxAmount: 0 }))
    : null;

/** Товары по суперцене в попапе «Спасибо за покупку» */
export const thankYouPromotionProductsQueryOptions = (
  orderId: number,
  date: string
) =>
  queryOptions({
    queryKey: ['thankYouPromotionProducts', orderId, date],
    queryFn: () => API.getThankYouPromotionProducts({ orderId, date }),
    select: selectThankYouPromotionProducts,
    staleTime: RECOMMENDATIONS_STALE_TIME,
    gcTime: RECOMMENDATIONS_GC_TIME
  });

const getUniqueElementsById = <T extends { id: string | number }>(
  arr: T[]
): T[] =>
  arr.filter(
    (item, index) => arr.findIndex(obj => obj.id === item.id) === index
  );

const selectRecommendationsToOrder = (
  res: Awaited<ReturnType<typeof API.getRecommendationsToOrder>>
) =>
  res.getRecommendationsToCart?.items?.length
    ? getUniqueElementsById(res.getRecommendationsToCart.items)
    : [];

/**
 * Карусель «Добавьте к заказу» на странице «Спасибо за покупку».
 * cartToken в ключе передаётся из компонента — импорт CartStore
 * из query-модуля дал бы цикл.
 */
export const recommendationsToOrderQueryOptions = (
  orderId: number,
  cartToken?: string | null
) =>
  queryOptions({
    queryKey: ['recommendationsToOrder', orderId, cartToken ?? null],
    queryFn: () => API.getRecommendationsToOrder({ orderId, cartToken }),
    select: selectRecommendationsToOrder,
    staleTime: RECOMMENDATIONS_STALE_TIME,
    gcTime: RECOMMENDATIONS_GC_TIME
  });
